import React, { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Modal as UiKitModal, notification } from '@gamesb42/ui-kit';

import urls from 'constants/urls';
import { deleteActivationOrderDataType } from 'queries/activationOrder/deleteActivationOrder';
import useActivationOrder, { activationOrderDataType, AoTypeEnum } from 'hooks/api/useActivationOrder';
import { changeStatusAONew } from 'api/ao';
import { AOStatus } from 'components/common/Status';
import Button from 'components/uiKit/Button';
import UploadAOChangeStatus from 'components/modal/UploadAOChangeStatus';
import plusSvg from 'assets/img/plus.svg';
import reloadSvg from 'assets/img/reload.svg';
import { Account } from 'types/ActivationOrderTypes';

import ClassicAO from './ClassicAO';
import ApplaudAO from './ApplaudAO';
import ActionsButton from './ActionsButton';
import FutureReceivablesAO from './FutureReceivablesAO';
import AvailableReceivablesOA from './AvailableReceivablesOA';
import styles from './styles.module.scss';
import FutureAO from './FutureAO';
import { useUserContext } from 'contexts/UserProvider';
import { isAdmin } from 'helpers/token';

type PropsT = {
  type: AoTypeEnum;
  activationOrderData: activationOrderDataType;
  deleteActivationOrder: (data: deleteActivationOrderDataType) => void;
  title: string;
  accounts: Account[];
  updateAccounts: (id: string) => void;
  setStatus?: any;
};

const General: FC<PropsT> = ({
  activationOrderData,
  deleteActivationOrder,
  type,
  title,
  accounts,
  updateAccounts,
  setStatus,
}) => {
  const history = useHistory();
  const { user } = useUserContext();
  const { getActivationOrder } = useActivationOrder();
  const [isSignOpen, setIsSignOpen] = useState(false);
  const isNew = activationOrderData?.status?.toUpperCase() === 'NEW';
  const isActiveNow =
    type === AoTypeEnum.AVAILABLE_RECEIVABLES && activationOrderData?.status?.toUpperCase() === 'ACTIVE';
  const isDeleted = activationOrderData?.status?.toUpperCase() === 'DELETED';
  const isRepaid = activationOrderData?.status?.toUpperCase() === 'REPAID';
  const isArchived = activationOrderData?.status?.toUpperCase() === 'ARCHIVE';
  // У погашенного FR карточка read-only, но меню с неактивным Edit AO по макету остаётся
  const isMenuVisible = isAdmin() && !isDeleted && (!isRepaid || type === AoTypeEnum.FUTURE);

  const refreshActivationOrder = () =>
    getActivationOrder({
      id: activationOrderData.id,
      type:
        type === AoTypeEnum.FUTURE_RECEIVABLES || type === AoTypeEnum.AVAILABLE_RECEIVABLES ? type : undefined,
    });

  const resetToNewAO = () =>
    changeStatusAONew(activationOrderData.id, 'NEW' as AOStatus)
      .then((result) => {
        setStatus(result || 'NEW');
        refreshActivationOrder();
        notification.success({
          message: `AO ${activationOrderData?.name ?? ''} reset to NEW successfully`.replace(/\s+/g, ' ').trim(),
          theme: 'dark',
          placement: 'bottomLeft',
        });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Ошибка при сбросе статуса AO:', error);
        notification.error({ message: 'Failed to reset AO to NEW', theme: 'dark', placement: 'bottomLeft' });
      });

  const confirmReset = () =>
    UiKitModal.warning({
      title: 'Are you sure reset this AO to NEW?',
      okText: 'Reset to NEW',
      cancelText: 'Cancel',
      okButtonProps: { style: { background: '#EC3E72', border: 'none' } },
      onOk: resetToNewAO,
      onCancel: () => {},
    });

  return (
    <div className={styles.wrapper}>
      {isMenuVisible && (
        <ActionsButton
          type={type}
          deleteActivationOrder={deleteActivationOrder}
          id={activationOrderData.id}
          title={title}
          companyId={activationOrderData?.companyId}
          setStatus={setStatus}
          currency={activationOrderData?.currency}
          amount={activationOrderData?.amount}
          status={activationOrderData?.status}
        />
      )}
      {AoTypeEnum.CLASSIC === type && (
        <ClassicAO
          activationOrderData={activationOrderData}
          accounts={accounts}
          updateAccounts={updateAccounts}
          setStatus={setStatus}
        />
      )}
      {AoTypeEnum.APPLAUD_FIX === type && <ApplaudAO activationOrderData={activationOrderData} accounts={accounts} />}
      {AoTypeEnum.FUTURE_RECEIVABLES === type && (
        <FutureReceivablesAO
          activationOrderData={activationOrderData}
          accounts={accounts}
          updateAccounts={updateAccounts}
        />
      )}
      {AoTypeEnum.AVAILABLE_RECEIVABLES === type && (
        <AvailableReceivablesOA activationOrderData={activationOrderData} accounts={accounts} setStatus={setStatus} />
      )}
      {AoTypeEnum.FUTURE === type && <FutureAO activationOrderData={activationOrderData} setStatus={setStatus} />}
      {/* GB42-3427 / GB42-3504: for CLASSIC and FUTURE the Sign action is rendered by AOActionsBar
          (from GET .../actions → POST .../actions/sign), so the legacy button below is skipped
          for these types to avoid a duplicate control. */}
      {isNew && type !== AoTypeEnum.CLASSIC && type !== AoTypeEnum.FUTURE && (
        <Button className={styles.generalBottom} variant="fill" onClick={() => setIsSignOpen(true)}>
          Sign AO
        </Button>
      )}
      {isActiveNow && (
        <Button
          className={styles.generalBottom}
          variant="fill"
          startAdornment={<img className="dropZoneGeneralImg" src={plusSvg} alt="plus" />}
          onClick={() => history.push(urls.getPurchaseRequestCreate())}
        >
          Create Request
        </Button>
      )}
      {isArchived && (
        <Button
          className={styles.generalBottom}
          variant="fill"
          style={{ '--bg-color': '#9197AD' } as React.CSSProperties}
          startAdornment={
            <img src={reloadSvg} alt="reload" style={{ filter: 'brightness(0) invert(1)' }} />
          }
          onClick={confirmReset}
        >
          Reset to NEW
        </Button>
      )}
      <UploadAOChangeStatus
        isOpen={isSignOpen}
        onClose={() => setIsSignOpen(false)}
        setStatus={setStatus}
        activationOrderId={activationOrderData.id}
        setLink={refreshActivationOrder}
      />
    </div>
  );
};

export default General;
